import { Box, Flex, List, ListItem, SimpleGrid, Text } from "@chakra-ui/react";
import { trackEvent } from "lib/analytics";
import { type Customer, type ResourcePage, type Banner } from "lib/sanity";
import Image from "next/image";
import React, { useState } from "react";
import Balancer from "react-wrap-balancer";
import ReactMarkdown from "react-markdown";
import { downloadUrl } from "utils";
import { Link } from "ui/link";
import { Section, sectionSpacingY } from "components/section";
import { Page } from "components/page";
import { HubSpotForm } from "components/hubspot";
import { Customers, StackedLogos } from "components/logos";
import { trackWhitepaperDownloaded } from "components/event-tracking";

type ResourceTemplateProps = {
  defaultLogos: Customer[];
  defaultMobileLogos: Customer[];
  announcementBanner?: Banner;
} & ResourcePage;

export function ResourcePageTemplate({
  defaultLogos,
  defaultMobileLogos,
  title,
  subtitle,
  downloadName,
  type,
  content,
  imageUrl,
  posterImageUrl,
  resourceFileUrl,
  resourceFileName,
  hubSpotFormId,
  hubSpotCompanyField,
  customers,
  metaTitle,
  metaDescription,
  partners,
  announcementBanner,
}: ResourceTemplateProps) {
  const [success, setSuccess] = useState<boolean>(false);
  const displayedHubspotFields = hubSpotCompanyField ? ["firstname", "lastname", "company"] : ["firstname", "lastname"];
  const fileExtension: string = resourceFileUrl.split(".").pop()!;
  const fileDownloadUrl = `${resourceFileUrl}?dl=${resourceFileName}.${fileExtension}`;

  const onFormSubmit = (lead: Record<string, string>) => {
    // Keep legacy video event
    if (type.name.toLowerCase() === "video") {
      trackEvent("Video Downloaded", {
        Email: lead.email,
        "First Name": lead.firstname,
        "Last Name": lead.lastname,
        page: window.location.href,
        file: title,
        type: type.name,
      });
    }

    // Track whitepaper download regardless of type
    trackWhitepaperDownloaded(title, lead, type.name);
    downloadUrl(fileDownloadUrl, resourceFileName);
    setSuccess(true);
  };

  return (
    <Page
      announcementBanner={announcementBanner}
      title={metaTitle ?? title}
      description={metaDescription ?? subtitle}
      image={
        imageUrl
          ? {
              width: 1200,
              height: 675,
              url: imageUrl,
              alt: title,
            }
          : undefined
      }
    >
      <Section mt={{ base: "bigger", md: "biggest" }} mb={{ base: "biggest", md: "huge" }}>
        {partners && partners.length > 0 && (
          <Flex display={{ base: "flex", lg: "none" }} align="center" w="100%" mb={8} gap={2}>
            <Text fontSize="xs" textStyle="uppercase">
              In partnership with
            </Text>
            <Flex gap={2} align="center">
              {partners.map((partner) => (
                <Box key={partner.name} position="relative" w="84px">
                  <Image
                    priority
                    src={partner.logoUrl + "?w=200"}
                    alt={`${partner.name}.`}
                    width={84}
                    height={30}
                    style={{
                      objectFit: "contain",
                      objectPosition: "left",
                      width: "100%",
                      height: "auto",
                      minWidth: "100%",
                      maxHeight: "30px",
                    }}
                  />
                </Box>
              ))}
            </Flex>
          </Flex>
        )}

        <SimpleGrid columns={{ base: 1, lg: 2 }} gap={{ base: 2, lg: 24 }}>
          <Box>
            <Text as="h1" mb={7} textStyle={{ base: "headline2", md: "headline1" }}>
              <Balancer>{title}</Balancer>
            </Text>
            <Flex direction="column" align="flex-start">
              <Text as="h2" mb={7} fontSize="brand.md" fontWeight="semibold">
                {subtitle}
              </Text>
              <Flex flex={1} flexDir="column" color="new.gray.900">
                <ReactMarkdown
                  components={{
                    a: ({ node, href, ...props }) => <Link href={href} variant="inline" {...props} />,
                    p: ({ node, ...props }) => <Text textStyle="article.p" fontFamily="nuckle" {...props} />,
                    strong: ({ children }) => (
                      <Text as="strong" fontWeight="bold" fontFamily="nuckle">
                        {children}
                      </Text>
                    ),
                    li: ({ children }) => (
                      <ListItem textStyle="article.listItem" fontFamily="nuckle">
                        {children}
                      </ListItem>
                    ),
                    ul: ({ children }) => (
                      <List textStyle="article.list" fontFamily="nuckle" listStyleType="disc">
                        {children}
                      </List>
                    ),
                  }}
                >
                  {content}
                </ReactMarkdown>
              </Flex>
            </Flex>
          </Box>

          <Flex direction="column" align={{ base: "start", lg: "center" }}>
            {partners && partners.length > 0 && (
              <Flex display={{ base: "none", lg: "flex" }} w="100%" align="center" justify="center" mb={8} gap={2}>
                <Text textStyle="uppercase" fontSize="sm">
                  In partnership with
                </Text>
                <Flex gap={2} align="center">
                  {partners.map((partner) => (
                    <Box key={partner.name} w="120px" position="relative">
                      <Image
                        priority
                        src={partner.logoUrl + "?w=300"}
                        alt={`${partner.name}.`}
                        width={120}
                        height={32}
                        style={{
                          objectFit: "contain",
                          objectPosition: "left",
                          width: "100%",
                          height: "auto",
                          minWidth: "100%",
                          maxHeight: "32px",
                        }}
                      />
                    </Box>
                  ))}
                </Flex>
              </Flex>
            )}

            <Box
              pos="relative"
              display={{ base: "none", lg: "inline-block" }}
              w="100%"
              position="relative"
              maxW="440px"
            >
              <Image
                priority
                src={posterImageUrl + "?w=800"}
                alt={`${title}.`}
                width={400}
                height={320}
                style={{
                  objectFit: "contain",
                  width: "100%",
                  height: "auto",
                  maxHeight: "320px",
                }}
              />
            </Box>

            <Box mt={{ base: 2, lg: 12 }} w="100%" maxW="400px">
              <Text textStyle="headline3" mb={4}>
                Download the {downloadName && <Text as="strong">{downloadName}</Text>} {type.name.toLowerCase()}
              </Text>
              <HubSpotForm
                displayedFields={displayedHubspotFields}
                label={`Download ${type.name.toLowerCase()}`}
                formId={hubSpotFormId}
                successContent={
                  success && (
                    <Text textStyle="caption1">
                      Thank you! If your download doesn&apos;t start immediately, please{" "}
                      <Link variant="inline" href={resourceFileUrl}>
                        click here
                      </Link>
                      .
                    </Text>
                  )
                }
                onSubmit={onFormSubmit}
              />
            </Box>
          </Flex>
        </SimpleGrid>
      </Section>

      {
        // If specific customer logos were provided, use those, otherwise show defaults
        customers && customers.length > 0 ? (
          <Customers
            customers={customers}
            title="The world’s most innovative companies choose Hightouch as their Customer Data and AI Decisioning Platform"
            my="0"
            pb={sectionSpacingY}
          />
        ) : (
          <StackedLogos customers={defaultLogos} mobileCustomers={defaultMobileLogos} my="0" pb={sectionSpacingY} />
        )
      }
    </Page>
  );
}
